import { useEffect, useMemo, useState } from "react";
import { api, formatINR, formatDate } from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/ui-helpers";
import { BookOpen, Search } from "lucide-react";
import { useClientPagination } from "@/hooks/useClientPagination";
import { PaginationControls } from "@/components/PaginationControls";

export default function Ledgers() {
  const [accounts, setAccounts] = useState(null);
  const [picked, setPicked] = useState(null);
  const [data, setData] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => { api.get("/ledger/accounts").then(r => setAccounts(r.data)); }, []);

  async function loadLedger(acc) {
    setPicked(acc); setData(null);
    const qs = new URLSearchParams({ account_type: acc.account_type, account_id: acc.account_id });
    if (fromDate) qs.set("from_date", fromDate);
    if (toDate) qs.set("to_date", toDate);
    const { data } = await api.get(`/ledger/entries?${qs}`);
    setData(data);
  }

  useEffect(() => { if (picked) loadLedger(picked); /* eslint-disable-next-line */ }, [fromDate, toDate]);

  const filteredAccounts = useMemo(() => {
    if (!accounts) return null;
    const f = (arr) => arr.filter(a => !search || (a.account_name || "").toLowerCase().includes(search.toLowerCase()));
    return {
      control_accounts: f(accounts.control_accounts || []),
      clients: f(accounts.clients || []),
      vendors: f(accounts.vendors || []),
    };
  }, [accounts, search]);

  const entries = data?.entries || [];
  const { paged, page, pageSize, total, setPage, setPageSize } = useClientPagination(entries, { initialPageSize: 50 });

  return (
    <div>
      <PageHeader
        eyebrow="Accounting"
        title="Ledgers"
        subtitle="Pick any account to see double-entry posted lines with running balance."
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Card className="lg:col-span-4 p-4 max-h-[700px] overflow-y-auto">
          <div className="relative mb-3">
            <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
            <input data-testid="ledger-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search account…"
              className="w-full border border-slate-300 bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#F97316]" />
          </div>
          {filteredAccounts && (
            <>
              <Section title="Control Accounts" accounts={filteredAccounts.control_accounts} picked={picked} onPick={loadLedger} />
              <Section title="Clients" accounts={filteredAccounts.clients} picked={picked} onPick={loadLedger} />
              <Section title="Vendors" accounts={filteredAccounts.vendors} picked={picked} onPick={loadLedger} />
            </>
          )}
        </Card>

        <Card className="lg:col-span-8 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-1">Ledger Account</div>
              <h3 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2">
                <BookOpen size={16} className="text-[#F97316]" />
                {picked?.account_name || "Select an account"}
              </h3>
            </div>
            <div className="flex items-center gap-2">
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="border border-slate-300 px-2 py-1.5 text-xs" placeholder="From" />
              <span className="text-xs text-slate-400">to</span>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="border border-slate-300 px-2 py-1.5 text-xs" placeholder="To" />
            </div>
          </div>

          {!picked ? (
            <EmptyState title="Select an account from the left" hint="Clients, vendors, cash/bank, TDS, GST and more." />
          ) : !data ? (
            <div className="p-10 text-center text-slate-500">Loading…</div>
          ) : data.entries.length === 0 ? (
            <EmptyState title="No entries for this account" />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="ledger-table">
                  <thead className="bg-slate-50 border-b-2 border-slate-200">
                    <tr>{["Date", "Particulars", "Ref", "Project", "Debit", "Credit", "Balance"].map(h =>
                      <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">{h}</th>
                    )}</tr>
                  </thead>
                  <tbody>
                    {paged.map(e => (
                      <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                        <td className="py-2.5 px-4 text-xs text-slate-600 whitespace-nowrap">{formatDate(e.entry_date)}</td>
                        <td className="py-2.5 px-4 text-sm text-slate-800">{e.narration}</td>
                        <td className="py-2.5 px-4 text-[10px] uppercase tracking-wider text-slate-500">{e.ref_type.replace(/_/g, " ")} {e.ref_number && `· ${e.ref_number}`}</td>
                        <td className="py-2.5 px-4 text-xs text-slate-600">{e.project_name || "—"}</td>
                        <td className="py-2.5 px-4 text-sm tabular text-slate-800">{e.debit ? formatINR(e.debit) : ""}</td>
                        <td className="py-2.5 px-4 text-sm tabular text-slate-800">{e.credit ? formatINR(e.credit) : ""}</td>
                        <td className={`py-2.5 px-4 text-sm tabular font-bold ${e.running_balance >= 0 ? "text-slate-800" : "text-[#F97316]"}`}>
                          {formatINR(Math.abs(e.running_balance))} <span className="text-[10px] font-normal text-slate-500">{e.running_balance >= 0 ? "Dr" : "Cr"}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50 border-t-2 border-slate-200 font-bold text-sm">
                    <tr>
                      <td colSpan={4} className="py-3 px-4 text-right uppercase tracking-wider text-[10px] text-slate-500">Totals</td>
                      <td className="py-3 px-4 tabular">{formatINR(data.total_debit)}</td>
                      <td className="py-3 px-4 tabular">{formatINR(data.total_credit)}</td>
                      <td className={`py-3 px-4 tabular ${data.closing_balance >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                        {formatINR(Math.abs(data.closing_balance))} {data.closing_balance >= 0 ? "Dr" : "Cr"}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <PaginationControls page={page} pageSize={pageSize} total={total}
                onPage={setPage} onPageSize={setPageSize}
                pageSizeOptions={[25, 50, 100, 200]} />
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

function Section({ title, accounts, picked, onPick }) {
  if (!accounts || accounts.length === 0) return null;
  return (
    <div className="mb-4">
      <div className="text-[9px] uppercase tracking-[0.3em] text-slate-500 font-bold mb-1.5 px-1">{title}</div>
      <div className="space-y-0.5">
        {accounts.map((a, i) => {
          const active = picked && picked.account_type === a.account_type && picked.account_id === a.account_id;
          return (
            <button
              key={`${a.account_type}-${a.account_id}-${i}`}
              data-testid={`ledger-acc-${a.account_type}-${a.account_id}`}
              onClick={() => onPick(a)}
              className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${active ? "bg-[#F97316] text-white" : "hover:bg-slate-100 text-slate-700"}`}
            >
              {a.code && <span className="text-[10px] font-bold opacity-70 mr-1.5">{a.code}</span>}
              {a.account_name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
